// File: /src/components/DashboardContent.js

import React, { useEffect, useMemo, useState } from 'react';
import {
	FaExclamationTriangle,
	FaExclamationCircle,
	FaInfoCircle,
	FaCheckCircle,
} from 'react-icons/fa';
import '../styles/DashboardContent.css';
import {
	DashboardIndicatorsProvider,
} from '../contexts/DashboardIndicatorsContext';
import DashboardHeader from './dashboard/DashboardHeader';
import SummaryCards from './dashboard/SummaryCards';
import FinanceOverviewCard from './dashboard/FinanceOverviewCard';
import StatusPieCard from './dashboard/StatusPieCard';
import IndicatorsSection from './dashboard/IndicatorsSection';
import ActivitiesPanel from './dashboard/ActivitiesPanel';
import AgendaPanel from './dashboard/AgendaPanel';
import SummaryBarChart from './dashboard/SummaryBarChart';

const DEFAULT_PIE_COLORS = [
	'#dc2626', // Pendente
	'#facc15', // EM ANDAMENTO
	'#16a34a', // Concluído
	'#2563eb',
	'#6b7280',
];
const STATUS_LABELS = {
	pending: 'Pendente',
	in_progress: 'Em Andamento',
	completed: 'Concluído',
	cancelled: 'Cancelado',
	paused: 'Pausado',
	indefinido: 'Indefinido',
};

const ALERT_ICONS = {
	danger: FaExclamationTriangle,
	warning: FaExclamationCircle,
	info: FaInfoCircle,
	success: FaCheckCircle,
};

const DashboardContent = ({
	user,
	loadingUser,
	clientsCount,
	projectsCount,
	invoicesCount,
	loadingSummary,
	summaryError,
	financeSummary,
	calendarEvents,
	loadingEvents,
	activities,
	loadingActivities,
	indicators,
	indicatorsLoading,
	projectStatusCounts,
	alerts = [],
}) => {
	const [chartSize, setChartSize] = useState({ innerRadius: 60, outerRadius: 80 });
	const [selectedDate, setSelectedDate] = useState(new Date());

	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth < 768) {
				setChartSize({ innerRadius: 40, outerRadius: 50 });
			} else {
				setChartSize({ innerRadius: 60, outerRadius: 80 });
			}
		};

		window.addEventListener('resize', handleResize);
		handleResize();
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const pieData = useMemo(() => {
		const counts = projectStatusCounts || {};
		const statusOrder = ['pending', 'in_progress', 'completed'];
		const data = [];

		statusOrder.forEach((status) => {
			const value = Number(counts[status]) || 0;
			if (value > 0) {
				data.push({
					name: STATUS_LABELS[status],
					value,
				});
			}
		});

		Object.entries(counts).forEach(([status, value]) => {
			if (statusOrder.includes(status)) return;
			const numericValue = Number(value) || 0;
			if (numericValue <= 0) return;

			data.push({
				name:
					STATUS_LABELS[status] ||
					status
						.replace(/_/g, ' ')
						.replace(/\b\w/g, (char) => char.toUpperCase()),
				value: numericValue,
			});
		});

		return data;
	}, [projectStatusCounts]);

	const barChartData = useMemo(
		() => [
			{ name: 'Clientes', value: clientsCount },
			{ name: 'Projetos', value: projectsCount },
			{ name: 'Faturas', value: invoicesCount },
		],
		[clientsCount, projectsCount, invoicesCount],
	);

	const dayEvents = useMemo(() => {
		return calendarEvents
			.filter((event) => {
				const eventDate = new Date(event.start);
				return (
					eventDate.getDate() === selectedDate.getDate() &&
					eventDate.getMonth() === selectedDate.getMonth() &&
					eventDate.getFullYear() === selectedDate.getFullYear()
				);
			})
			.sort((a, b) => new Date(a.start) - new Date(b.start));
	}, [calendarEvents, selectedDate]);

	const goToPreviousDay = () => {
		setSelectedDate((prev) => {
			const next = new Date(prev);
			next.setDate(prev.getDate() - 1);
			return next;
		});
	};

	const goToNextDay = () => {
		setSelectedDate((prev) => {
			const next = new Date(prev);
			next.setDate(prev.getDate() + 1);
			return next;
		});
	};

	const setToday = () => setSelectedDate(new Date());

	return (
		<DashboardIndicatorsProvider value={{ indicators, loading: indicatorsLoading }}>
			<div className="dashboard-container">
				<DashboardHeader user={user} loading={loadingUser} />

			{loadingSummary && (
				<p className="loading-text">
					Carregando...
					<br />
					<span className="loading-spinner"></span>
				</p>
			)}

			{summaryError && (
				<div className="error-message">
					<p>{summaryError}</p>
					<button 
						onClick={() => window.location.reload()} 
						className="error-reload-button"
					>
						Recarregar Página
					</button>
				</div>
			)}

			{!loadingSummary && !summaryError && (
						<>
							{alerts.length > 0 && (
								<section
									className="dashboard-insights"
									aria-label="Indicadores operacionais"
								>
									{alerts.map((alert) => {
										const Icon =
											ALERT_ICONS[alert.type] ||
											FaInfoCircle;

										return (
											<article
												key={alert.id}
												className={`dashboard-insight-card dashboard-insight-card--${alert.type}`}
												aria-live="polite"
											>
												<div className="dashboard-insight__icon">
													<Icon />
												</div>
												<div className="dashboard-insight__body">
													<header className="dashboard-insight__header">
														{typeof alert.count === 'number' && (
															<span className="dashboard-insight__value">
																{alert.count.toLocaleString('pt-BR')}
															</span>
														)}
														<h3 className="dashboard-insight__label">
															{alert.label || alert.title}
														</h3>
													</header>
													{alert.message && (
														<p className="dashboard-insight__description">
															{alert.message}
														</p>
													)}
												</div>
											</article>
										);
									})}
								</section>
							)}

							<div className="cards-container">
							<SummaryCards
								clientsCount={clientsCount}
								projectsCount={projectsCount}
								invoicesCount={invoicesCount}
							/>
							<FinanceOverviewCard financeSummary={financeSummary} />
							<StatusPieCard
								pieData={pieData}
								pieColors={DEFAULT_PIE_COLORS}
								chartSize={chartSize}
							/>
						</div>

						<div className="agenda-section">
							<AgendaPanel
								selectedDate={selectedDate}
								setToday={setToday}
								goToPreviousDay={goToPreviousDay}
								goToNextDay={goToNextDay}
								loadingEvents={loadingEvents}
								dayEvents={dayEvents}
							/>
							<IndicatorsSection />
						</div>

						<div className="stats-container">
							<ActivitiesPanel
								activities={activities}
								loading={loadingActivities}
							/>
							<SummaryBarChart data={barChartData} />
						</div>
					</>
				)}
			</div>
		</DashboardIndicatorsProvider>
	);
};

export default DashboardContent;
